import './styles.css';
import { hasStaffAppContext } from './clinic/seed';
import { isStaffAppPath } from './clinic/nav';
import { el } from './dom';
import type { Route } from './router';
import { bindStaffContextRepaint, currentHashPath, renderStaffAppShell } from './shell/app-shell';
import { consumeStaffSignedOut } from './staff/token';
import { renderAdmin } from './views/admin';
import { renderCalendar } from './views/calendar';
import { renderPlaceholder } from './views/placeholder';
import { renderProfile } from './views/profile';
import {
  oauthCallbackPending,
  renderBreakGlass,
  renderStaff,
  renderStaffCallback,
} from './views/staff';

/** Door is the only public landing. Status/Lane tabs are not the home. */
const DOOR_ROUTES: readonly Route[] = [
  { path: '/', label: 'Door', render: renderStaff },
  { path: '/staff', label: 'Door', render: renderStaff },
  { path: '/break-glass', label: 'Break-glass', render: renderBreakGlass },
];

const STAFF_APP_ROUTES: readonly Route[] = [
  { path: '/calendar', label: 'Calendar', render: renderCalendar },
  {
    path: '/patients',
    label: 'Patients',
    render: renderPlaceholder({
      title: 'Patients',
      section: 'BOOKING / EMR',
      body: 'Patient list and chart demographics are later slices. No PHI is stored in this preview.',
    }),
  },
  {
    path: '/chart',
    label: 'Chart / EMR',
    render: renderPlaceholder({
      title: 'Chart / EMR',
      section: 'BOOKING / EMR',
      body: 'Encounter and chart routes stay 501 on the API. This page is chrome only.',
    }),
  },
  {
    path: '/service-center',
    label: 'Service Center',
    render: renderPlaceholder({
      title: 'Service Center',
      section: 'ORDERING',
      body: 'Service Center CRUD is out of scope. Stubbed so ORDERING matches the live menu.',
    }),
  },
  {
    path: '/orders',
    label: 'Sales & Order History',
    render: renderPlaceholder({
      title: 'Sales & Order History',
      section: 'ORDERING',
      body: 'Order history is not wired. No live Stripe charges in this preview.',
    }),
  },
  {
    path: '/checkout',
    label: 'Checkout',
    render: renderPlaceholder({
      title: 'Checkout',
      section: 'ORDERING',
      body: 'POS / checkout is out of scope. The nav label matches Lovable.',
    }),
  },
  { path: '/admin', label: 'Staff & locations', render: renderAdmin },
  {
    path: '/provider',
    label: 'Provider home',
    render: renderPlaceholder({
      title: 'Provider',
      section: 'PROVIDER',
      body: 'Provider tools are not built yet. This group matches the live collapsed section.',
    }),
  },
  {
    path: '/md-oversight',
    label: 'Oversight home',
    render: renderPlaceholder({
      title: 'MD Oversight',
      section: 'MD OVERSIGHT',
      body: 'Medical-director oversight is not built yet. This group matches the live collapsed section.',
    }),
  },
  { path: '/profile', label: 'My Profile', render: renderProfile },
  {
    path: '/support',
    label: 'Support',
    render: renderPlaceholder({
      title: 'Support',
      section: 'Account',
      body: 'Support inbox is not wired on this preview host.',
    }),
  },
];

const mount = document.querySelector<HTMLElement>('#app');
if (!mount) {
  throw new Error('Missing #app mount point');
}
const app: HTMLElement = mount;

function findRoute(routes: readonly Route[], path: string): Route | undefined {
  return routes.find((route) => route.path === path);
}

function paintDoor(path: string): void {
  const root = el('div', { class: 'door-root' });
  const outlet = el('main', { class: 'door-outlet', id: 'main' });
  root.append(outlet);
  app.replaceChildren(root);
  const route = findRoute(DOOR_ROUTES, path) ?? DOOR_ROUTES[0];
  route?.render(outlet);
}

function paintStaffApp(path: string): void {
  const outlet = el('main', { class: 'outlet', id: 'main' });
  renderStaffAppShell(app, outlet, path);
  const route = findRoute(STAFF_APP_ROUTES, path);
  if (route) {
    route.render(outlet);
  }
}

function paint(): void {
  if (oauthCallbackPending()) {
    const root = el('div', { class: 'door-root' });
    const outlet = el('main', { class: 'door-outlet', id: 'main' });
    root.append(outlet);
    app.replaceChildren(root);
    renderStaffCallback(outlet);
    return;
  }

  resumeDoorAfterLogout();

  const path = currentHashPath();

  // Already signed in + has shell context → calendar/shell
  if (isStaffAppPath(path)) {
    if (!hasStaffAppContext()) {
      window.location.hash = '/';
      return;
    }
    paintStaffApp(path);
    return;
  }

  // Soft: signed-in session with no hash shell yet still shows door (Open calendar)
  // Unknown preview paths (old Status/About) → door
  if (path === '/about' || path === '/status' || path.startsWith('/status')) {
    window.location.hash = '/';
    return;
  }

  paintDoor(path === '/staff' || path === '/break-glass' ? path : '/');
}

/** Cognito logout_uri is origin root — land full-bleed door. */
function resumeDoorAfterLogout(): void {
  if (!consumeStaffSignedOut()) {
    return;
  }
  const path = window.location.hash.replace(/^#/, '') || '/';
  if (path === '/' || path === '') {
    return;
  }
  window.location.hash = '/';
}

window.addEventListener('hashchange', paint);
bindStaffContextRepaint(paint);
paint();
